import { AlertTriangle, Trash2 } from '@/components/icons';
import { Button } from './beui';
import { Loader } from './beui/loader';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './dialog';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDeleting?: boolean;
}

export default function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isDeleting = false,
}: DeleteConfirmModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isDeleting) onClose(); }}>
      <DialogContent
        tabIndex={-1}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus();
        }}
        className="w-[calc(100%-2rem)] max-w-[420px] gap-0 overflow-hidden rounded-2xl border-border bg-card p-0 shadow-2xl outline-none"
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-red-500/10 text-destructive">
              <Trash2 className="h-[18px] w-[18px]" />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5 pr-6">
              <DialogTitle className="text-[16px] font-semibold tracking-tight text-foreground">
                {title}
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-5 text-muted-foreground">
                {message}
              </DialogDescription>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5 flex-none text-destructive" />
            <span>This action cannot be undone.</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4">
          <Button
            variant="ghost"
            size="sm"
            pressScale={0.97}
            onClick={onClose}
            disabled={isDeleting}
            className="hover:bg-muted"
          >
            {cancelText}
          </Button>
          <Button
            size="sm"
            pressScale={0.97}
            onClick={onConfirm}
            disabled={isDeleting}
            aria-busy={isDeleting}
            className="bg-destructive text-destructive-foreground hover:opacity-90"
          >
            {isDeleting && <Loader size={14} label="Deleting" className="text-current" />}
            <span>{isDeleting ? 'Deleting…' : confirmText}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
